import { ChangeDetectorRef, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { QuizState } from 'src/app/quiz/state/quiz.state';
import { EngineState } from 'src/app/engine/state/engine.state';
import { Fullscreen } from 'src/app/shared/libraries/fullscreen/fullscreen';
import { ChapterReadyStateModel } from 'src/app/story/models/chapter-ready-state.model';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { HelpTypeEnum } from '../../../models/help-type.enum';
import { ViewUI } from '../../../models/view-ui.enum';
import { ViewUIService } from '../../../view-ui.service';
import { ViewService } from '../../../view.service';
import { EngineService } from 'src/app/engine/engine.service';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { ViewState } from '../../../state/view.state';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { NativeEnvironmentPlatform } from 'src/environments/models/native-environment-platform';
import { QuizService } from '../../quiz/view-quiz.service';
import { TreeSearchDisplayMode } from 'src/app/shared/components/tree/tree-search/tree-search.component';

@Component({
  selector: 'app-view-menu',
  templateUrl: './view-menu.component.html',
  styleUrls: ['./view-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewMenuComponent implements OnInit, OnDestroy {
  application = EnvironmentApplication;
  nativePlatform = NativeEnvironmentPlatform;
  
  [x: string]: any;
  viewUI = ViewUI;

  // ---------- Observables ----------
  @Select(StoryChaptersState.getSelectedChapterReadyState)
  selectedChapterReady$: Observable<ChapterReadyStateModel>;

  @Select(QuizState.hasQuiz)
  hasQuiz$: Observable<boolean>;

  @Select(QuizState.getQuizEnabled)
  quizEnabled$: Observable<boolean>;

  @Select(QuizState.getQuizModeLocked)
  quizLocked$: Observable<boolean>;

  @Select(QuizState.getQuizModeActive)
  quizActive$: Observable<boolean>;
  
  @Select(EngineState.getShowLabels)
  showLabels$: Observable<boolean>;

  // ---------- Class Variables ----------
  isFullscreen = false;
  canFullscreen = false;
  fullscreen$: Observable<Event>;
  ngUnsubscribe = new Subject();
  visibilityLayersVisible = false;

  constructor(
    private viewUIService: ViewUIService,
    private viewService: ViewService,
    private quizService: QuizService,
    private engineService: EngineService,
    private changeDetectorRef: ChangeDetectorRef,
    private store: Store
  ) { }

  ngOnInit() {
    this.canFullscreen = Fullscreen.canFullscreen();

    this.initFullscreenListener();
    this.initLayoutListener();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initFullscreenListener() {
    this.fullscreen$ = Fullscreen.onFullscreenChange();
    this.fullscreen$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(event => {
        this.isFullscreen = Fullscreen.isFullscreen();
        this.changeDetectorRef.markForCheck();
      });
  }

  initLayoutListener() {
    this.store
      .select(ViewState.getUI)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.visibilityLayersVisible = this.viewUIService.isVisible(ViewUI.VisibilityLayers);
        this.changeDetectorRef.markForCheck();
      });
  }

  handleToggleLabelsClick() {
    this.viewService.toggleLabels();
  }

  handleFullscreenClick() {
    Fullscreen.toggleFullscreen();
  }
  
  handleTutorialClick() {
    this.viewService.toggleTutorial(true);
  }

  handleTakeQuizClick() {
    this.quizService.startQuiz();
  }

  handleHelpBasicsClick() {
    this.viewService.toggleHelp(true, HelpTypeEnum.Basics);
  }

  handleHelpShortcutsClick() {
    this.viewService.toggleHelp(true, HelpTypeEnum.Shortcuts);
  }

  handlePresentClick() {
    this.viewService.togglePresentation();
  }

  handleNewTabClick() {
    this.viewService.openInNewTab();
  }

  handleSearchClick() {
    this.viewService.toggleSearch(TreeSearchDisplayMode.List, true);
  }

  handleTreeClick() {
    this.viewService.toggleSearch(TreeSearchDisplayMode.Tree, true);
  }

  handleDrawClick() {
    this.viewService.toggleWhiteboard(true);
  }

  toggleToolbar(bar: 'media' | 'tools' | 'toolbar' | 'visibilityLayers') {
    this.viewService.toggleToolbar(bar);
  }

  isEnabled(layout: ViewUI) {
    return this.viewUIService.isUIEnabled(layout);
  }

  isVisible(layout: ViewUI) {
    return this.viewUIService.isVisible(layout);
  }
}
